import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  Brain,
  Clock,
  Coins,
  Loader2,
  MessageSquare,
  MessageSquarePlus,
  MoreHorizontal,
  Pencil,
  Send,
  Sparkles,
  Trash2,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAccount } from "@/contexts/account.context";
import { useAuth } from "@/contexts/auth.context";
import { BuyAiCreditsDialog } from "@/components/ai-credits/BuyAiCreditsDialog";
import { CreditsExhaustedNotice } from "@/components/ai-credits/CreditsExhaustedNotice";
import { ehAssinante } from "@/lib/account-access-context";
import { tenantAtivo } from "@/lib/hey-api-client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  mediaChatService,
  type AICredits,
  type ConversationSummary,
  type MediaChatJob,
  type MediaChatJobStatus,
} from "@/services/media-chat.service";
import { AiCreditsExhaustedError } from "@/services/ai-credit-packs.service";

const POLL_INTERVAL_MS = 1200;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  jobId?: string;
  status?: MediaChatJobStatus;
  phaseLabel?: string;
  phaseSources?: string[];
  error?: string | null;
  /** <M-280> O erro foi o `402`: a loja ficou sem créditos de IA. */
  creditsExhausted?: boolean;
}

// Fontes que o Arquiteto consulta, para mostrar de onde vêm os números enquanto
// ele trabalha. As chaves são as mesmas de SOURCE_LABELS no nexus
// (media_chat_agent_service.py); fonte desconhecida simplesmente não é exibida.
const SOURCE_BADGES: Record<string, { label: string; logo?: string }> = {
  ga4: { label: "Google Analytics", logo: "/analytics.png" },
  google_ads: { label: "Google Ads", logo: "/googleAds.png" },
  meta_ads: { label: "Meta Ads", logo: "/metaAds.png" },
  search_console: { label: "Search Console" },
  instagram: { label: "Instagram" },
};

function SourceBadges({ sources }: { sources?: string[] }) {
  const badges = (sources ?? [])
    .map((source) => SOURCE_BADGES[source])
    .filter(Boolean) as { label: string; logo?: string }[];
  if (badges.length === 0) return null;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {badges.map((badge) => (
        <span
          key={badge.label}
          className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-2 py-0.5 text-[11px] text-muted-foreground"
        >
          {badge.logo && (
            <img src={badge.logo} alt="" aria-hidden className="h-3 w-3 object-contain" />
          )}
          {badge.label}
        </span>
      ))}
    </div>
  );
}

// Status do processamento da IA na mensagem do usuário (comportamento em tempo real).
function statusMeta(status?: MediaChatJobStatus): {
  label: string;
  icon: JSX.Element;
} {
  switch (status) {
    case "QUEUED":
      return { label: "Na fila", icon: <Clock className="h-3.5 w-3.5" /> };
    case "PROCESSING":
      return {
        label: "Analisando",
        icon: <Brain className="h-3.5 w-3.5 animate-pulse" />,
      };
    case "COMPLETED":
      return { label: "Analisado", icon: <Sparkles className="h-3.5 w-3.5" /> };
    case "FAILED":
      return {
        label: "Falhou",
        icon: <AlertCircle className="h-3.5 w-3.5 text-destructive" />,
      };
    default:
      return { label: "", icon: <Clock className="h-3.5 w-3.5" /> };
  }
}

let messageCounter = 0;
const nextId = () => `msg-${Date.now()}-${messageCounter++}`;

export default function MediaChatPage() {
  const { selectedAccount } = useAccount();
  const accountId = selectedAccount?.id;
  // <M-280> Só o dono da conta compra créditos — é ele quem paga —, e para a loja ativa.
  const { user } = useAuth();
  const lojaAtiva = selectedAccount?.customer_uuid ?? tenantAtivo() ?? null;
  const podeComprarCreditos = ehAssinante(user) && lojaAtiva !== null;
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [credits, setCredits] = useState<AICredits | null>(null);

  // Modais
  const [renameTarget, setRenameTarget] = useState<ConversationSummary | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ConversationSummary | null>(null);

  const activeIdRef = useRef<string | null>(null);
  const activePollsRef = useRef<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  useEffect(() => {
    const active = activePollsRef.current;
    return () => active.clear();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const patchMessage = useCallback((id: string, patch: Partial<ChatMessage>) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }, []);

  const loadHistory = useCallback(async (conversationId: string) => {
    const history = await mediaChatService.getHistory(conversationId);
    setMessages(
      history.map((m, i) => ({
        id: `hist-${conversationId}-${i}`,
        role: m.role,
        content: m.content,
      })),
    );
  }, []);

  const selectConversation = useCallback(
    async (conversationId: string) => {
      activePollsRef.current.clear();
      setIsBusy(false);
      setActiveId(conversationId);
      setMessages([]);
      try {
        await loadHistory(conversationId);
      } catch {
        setMessages([]);
      }
    },
    [loadHistory],
  );

  // Ao montar / trocar de conta: lista as conversas e seleciona a mais recente
  // (cria uma se a conta ainda não tiver nenhuma).
  const loadConversations = useCallback(async () => {
    activePollsRef.current.clear();
    setIsBusy(false);
    try {
      const { conversations: list } = await mediaChatService.listConversations(accountId);
      setConversations(list);
      if (list.length > 0) {
        await selectConversation(list[0].conversationId);
      } else {
        const id = await mediaChatService.createConversation(accountId);
        setConversations([{ conversationId: id, title: null, updatedAt: null }]);
        setActiveId(id);
        setMessages([]);
      }
    } catch {
      setConversations([]);
      setActiveId(null);
      setMessages([]);
    }
  }, [accountId, selectConversation]);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  const loadCredits = useCallback(async () => {
    try {
      setCredits(await mediaChatService.getCredits());
    } catch {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    void loadCredits();
  }, [loadCredits]);

  const refreshList = useCallback(async () => {
    try {
      const { conversations: list } = await mediaChatService.listConversations(accountId);
      setConversations(list);
    } catch {
      /* silencioso */
    }
  }, [accountId]);

  const handleNewConversation = useCallback(async () => {
    // Se a conversa ativa está vazia, apenas mantém (evita criar várias vazias).
    if (activeId && messages.length === 0) return;
    try {
      const id = await mediaChatService.createConversation(accountId);
      activePollsRef.current.clear();
      setIsBusy(false);
      setActiveId(id);
      setMessages([]);
      await refreshList();
    } catch (err) {
      toast({
        title: "Limite de conversas",
        description:
          err instanceof Error ? err.message : "Não foi possível criar a conversa.",
        variant: "destructive",
      });
    }
  }, [accountId, activeId, messages.length, refreshList]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const conversationId = deleteTarget.conversationId;
    setDeleteTarget(null);
    try {
      await mediaChatService.deleteConversation(conversationId);
      const remaining = conversations.filter((c) => c.conversationId !== conversationId);
      setConversations(remaining);
      if (activeId === conversationId) {
        if (remaining.length > 0) {
          await selectConversation(remaining[0].conversationId);
        } else {
          await handleNewConversation();
        }
      }
    } catch {
      toast({ title: "Erro", description: "Não foi possível apagar.", variant: "destructive" });
    }
  }, [deleteTarget, conversations, activeId, selectConversation, handleNewConversation]);

  const submitRename = useCallback(async () => {
    if (!renameTarget || !renameValue.trim()) return;
    const conversationId = renameTarget.conversationId;
    const title = renameValue.trim();
    setRenaming(true);
    try {
      await mediaChatService.renameConversation(conversationId, title);
      setConversations((prev) =>
        prev.map((c) => (c.conversationId === conversationId ? { ...c, title } : c)),
      );
      setRenameTarget(null);
    } catch {
      toast({ title: "Erro", description: "Não foi possível renomear.", variant: "destructive" });
    } finally {
      setRenaming(false);
    }
  }, [renameTarget, renameValue]);

  const pollJob = useCallback(
    (jobId: string, userMsgId: string, assistantMsgId: string) => {
      activePollsRef.current.add(jobId);
      const startedAt = Date.now();

      const finishTimeout = () => {
        activePollsRef.current.delete(jobId);
        patchMessage(userMsgId, { status: "FAILED" });
        patchMessage(assistantMsgId, {
          status: "FAILED",
          error: "Tempo de resposta excedido.",
        });
        setIsBusy(false);
      };

      const tick = async () => {
        if (!activePollsRef.current.has(jobId)) return;
        let job: MediaChatJob;
        try {
          job = await mediaChatService.getJobStatus(jobId);
        } catch {
          if (!activePollsRef.current.has(jobId)) return;
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) return finishTimeout();
          window.setTimeout(tick, POLL_INTERVAL_MS);
          return;
        }
        if (!activePollsRef.current.has(jobId)) return;

        const lastPhase = job.phases[job.phases.length - 1];
        patchMessage(userMsgId, { status: job.status });
        patchMessage(assistantMsgId, {
          status: job.status,
          phaseLabel: lastPhase?.label,
          phaseSources: lastPhase?.sources,
        });

        if (job.status === "COMPLETED") {
          activePollsRef.current.delete(jobId);
          patchMessage(assistantMsgId, {
            content: job.answer ?? "",
            phaseLabel: undefined,
            phaseSources: undefined,
          });
          setIsBusy(false);
          return;
        }
        if (job.status === "FAILED") {
          activePollsRef.current.delete(jobId);
          patchMessage(assistantMsgId, {
            error: job.error ?? "Não foi possível gerar a resposta.",
            phaseLabel: undefined,
            phaseSources: undefined,
          });
          setIsBusy(false);
          return;
        }
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) return finishTimeout();
        window.setTimeout(tick, POLL_INTERVAL_MS);
      };

      window.setTimeout(tick, POLL_INTERVAL_MS);
    },
    [patchMessage],
  );

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isBusy) return;

    setInput("");
    setIsBusy(true);

    const userMsgId = nextId();
    const assistantMsgId = nextId();
    const wasEmpty = messages.length === 0;
    setMessages((prev) => [
      ...prev,
      { id: userMsgId, role: "user", content: text, status: "QUEUED" },
      {
        id: assistantMsgId,
        role: "assistant",
        content: "",
        status: "QUEUED",
        phaseLabel: "Na fila",
      },
    ]);

    try {
      let conversationId = activeIdRef.current;
      if (!conversationId) {
        conversationId = await mediaChatService.createConversation(accountId);
        setActiveId(conversationId);
      }
      const job = await mediaChatService.ask(conversationId, text);
      patchMessage(userMsgId, { jobId: job.jobId, status: job.status });
      patchMessage(assistantMsgId, { jobId: job.jobId, status: job.status });
      if (job.creditsRemaining != null) {
        setCredits((prev) => ({
          unlimited: prev?.unlimited ?? false,
          balance: job.creditsRemaining ?? null,
        }));
      }
      pollJob(job.jobId, userMsgId, assistantMsgId);
      if (wasEmpty) void refreshList();
    } catch (err) {
      patchMessage(userMsgId, { status: "FAILED" });
      patchMessage(assistantMsgId, {
        status: "FAILED",
        error:
          err instanceof Error ? err.message : "Não foi possível enviar a mensagem.",
        creditsExhausted: err instanceof AiCreditsExhaustedError,
        phaseLabel: undefined,
      });
      setIsBusy(false);
      void loadCredits();
    }
  }, [input, isBusy, messages.length, accountId, patchMessage, pollJob, refreshList, loadCredits]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  return (
    <div className="p-6 space-y-6 page-background">
      {/* Cabeçalho padrão da plataforma */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Arquiteto</h1>
          <p className="text-muted-foreground">
            Pergunte em linguagem natural sobre faturamento, vendas e mídia paga.
          </p>
        </div>
        {credits && (
          <div
            className="flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-sm text-muted-foreground"
            title="Créditos de IA — cada pergunta consome 1 crédito"
          >
            <Coins className="h-4 w-4 text-amber-500" />
            <span className="font-medium text-foreground">
              {credits.unlimited ? "Ilimitado" : (credits.balance ?? 0)}
            </span>
            {!credits.unlimited && <span className="hidden sm:inline">créditos</span>}
            {!credits.unlimited && podeComprarCreditos && (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 pl-1"
                onClick={() => setBuyCreditsOpen(true)}
              >
                Comprar
              </Button>
            )}
          </div>
        )}
      </div>

      {podeComprarCreditos && lojaAtiva && (
        <BuyAiCreditsDialog
          open={buyCreditsOpen}
          onOpenChange={setBuyCreditsOpen}
          customerUuid={lojaAtiva}
          storeName={selectedAccount?.name ?? null}
          onPurchased={() => void loadCredits()}
        />
      )}

      <div className="flex h-[calc(100vh-14rem)] gap-4">
      {/* Sidebar de conversas */}
      <Card className="hidden w-72 shrink-0 flex-col overflow-hidden md:flex">
        <div className="border-b p-3">
          <Button
            onClick={() => void handleNewConversation()}
            className="w-full justify-start gap-2"
          >
            <MessageSquarePlus className="h-4 w-4" />
            Nova conversa
          </Button>
        </div>
        <div className="flex-1 space-y-0.5 overflow-y-auto p-2">
          {conversations.length === 0 ? (
            <p className="px-2 py-8 text-center text-xs text-muted-foreground">
              Nenhuma conversa ainda.
            </p>
          ) : (
            conversations.map((c) => {
              const isActive = c.conversationId === activeId;
              return (
                <div
                  key={c.conversationId}
                  className={cn(
                    "group flex items-center gap-1 rounded-lg px-2 py-2 text-sm transition-colors",
                    isActive ? "bg-muted" : "hover:bg-muted/60",
                  )}
                >
                  <button
                    onClick={() => void selectConversation(c.conversationId)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    title={c.title ?? "Nova conversa"}
                  >
                    <MessageSquare
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isActive ? "text-primary" : "text-muted-foreground",
                      )}
                    />
                    <span
                      className={cn(
                        "truncate",
                        isActive
                          ? "font-medium text-foreground"
                          : "text-muted-foreground",
                      )}
                    >
                      {c.title ?? "Nova conversa"}
                    </span>
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          "shrink-0 rounded-md p-1 text-muted-foreground transition-opacity hover:text-foreground",
                          isActive
                            ? "opacity-100"
                            : "opacity-0 focus:opacity-100 group-hover:opacity-100",
                        )}
                        aria-label="Ações da conversa"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem
                        onClick={() => {
                          setRenameValue(c.title ?? "");
                          setRenameTarget(c);
                        }}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Renomear
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setDeleteTarget(c)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Apagar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* Área do chat */}
      <div className="flex min-w-0 flex-1 flex-col">
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4">
            {messages.length === 0 && (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                <Sparkles className="h-8 w-8 opacity-40" />
                <p className="max-w-sm text-sm">
                  Experimente perguntar:{" "}
                  <span className="font-medium text-foreground">
                    “Qual foi o faturamento dos últimos 3 meses?”
                  </span>
                </p>
              </div>
            )}
            {messages.map((message) =>
              message.role === "user" ? (
                <UserBubble key={message.id} message={message} />
              ) : (
                <AssistantBubble
                  key={message.id}
                  message={message}
                  onBuyCredits={
                    podeComprarCreditos ? () => setBuyCreditsOpen(true) : undefined
                  }
                />
              ),
            )}
          </div>

          <div className="border-t p-3">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Pergunte sobre faturamento, ROAS, produtos mais vendidos..."
                rows={1}
                className="max-h-32 min-h-[44px] resize-none"
              />
              <Button
                onClick={() => void handleSend()}
                disabled={isBusy || !input.trim()}
                size="icon"
                className="h-11 w-11 shrink-0"
              >
                {isBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </Card>
      </div>
      </div>

      {/* Modal: renomear */}
      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => !open && setRenameTarget(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Renomear conversa</DialogTitle>
            <DialogDescription>
              Escolha um nome para identificar esta conversa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="conversation-title">Nome</Label>
            <Input
              id="conversation-title"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void submitRename();
                }
              }}
              placeholder="Ex.: Análise de faturamento"
              maxLength={200}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() => void submitRename()}
              disabled={renaming || !renameValue.trim()}
            >
              {renaming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: confirmar exclusão */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar conversa?</AlertDialogTitle>
            <AlertDialogDescription>
              A conversa{" "}
              <span className="font-medium text-foreground">
                “{deleteTarget?.title ?? "Nova conversa"}”
              </span>{" "}
              será removida da sua lista. Esta ação não pode ser desfeita por aqui.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <AlertDialogAction
              onClick={() => void confirmDelete()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Apagar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UserBubble({ message }: { message: ChatMessage }) {
  const meta = statusMeta(message.status);
  return (
    <div className="flex justify-end">
      <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-primary px-4 py-2 text-primary-foreground">
        <p className="whitespace-pre-line text-sm">{message.content}</p>
        {meta.label && (
          <div className="mt-1 flex items-center justify-end gap-1 text-[11px] text-primary-foreground/70">
            <span>{meta.label}</span>
            {meta.icon}
          </div>
        )}
      </div>
    </div>
  );
}

function AssistantBubble({
  message,
  onBuyCredits,
}: {
  message: ChatMessage;
  onBuyCredits?: () => void;
}) {
  const isPending =
    !message.content &&
    !message.error &&
    (message.status === "QUEUED" || message.status === "PROCESSING");
  const isEmptyDone = !message.content && !message.error && !isPending;

  return (
    <div className="flex justify-start">
      <div
        className={cn(
          "max-w-[80%] rounded-2xl rounded-bl-sm bg-muted px-4 py-2",
          message.error && !message.creditsExhausted && "bg-destructive/10",
          message.creditsExhausted && "border border-amber-500/40 bg-amber-500/10",
        )}
      >
        {isPending && (
          <div className="text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>{message.phaseLabel ?? "Processando"}…</span>
            </div>
            <SourceBadges sources={message.phaseSources} />
          </div>
        )}
        {message.error && message.creditsExhausted && (
          <CreditsExhaustedNotice detail={message.error} onBuyCredits={onBuyCredits} />
        )}
        {message.error && !message.creditsExhausted && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>{message.error}</span>
          </div>
        )}
        {message.content && (
          <div className="prose prose-sm max-w-none leading-relaxed dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
          </div>
        )}
        {isEmptyDone && (
          <span className="text-sm text-muted-foreground">
            Não recebi uma resposta. Tente reformular a pergunta.
          </span>
        )}
      </div>
    </div>
  );
}
